import type { FeatureCollection } from "@deck.gl-community/editable-layers";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import Supercluster from "supercluster";
import type {
    DataSurvey,
    MapExtraDataSurveysListDataTypeEnum,
    MapExtraDataSurveysListRequest,
} from "../../../apiClient/generated";
import { useAppSelector, useMapApiClient } from "../../../hooks";
import { createFeatureCollection } from "../../../utils/geopatialUtils";
import { MAP_EXTRA_CLUSTERING_LIMIT } from "../constants";
import type { FilterByAreaDef } from "../hooks";
import { ChampionXLayer, ExtraDataLayer } from "../layers/extra";
import { useStable } from "./areaFilter";
import { useMapDataLoader } from "./dataLoader";
import { useMapData } from "./mapDataAndFilters";
import { useMap } from "./mapState";
import { cleanFilters } from "./utils";

/**
 * Extra data
 *
 * All other providers that don't fit into our
 * definition of emissions go here.
 */
export const useExtraDataOnMap = (
    enabled: boolean,
    dataName: string,
    filters: {
        dataType?: Array<MapExtraDataSurveysListDataTypeEnum>;
        endDatetimeAfter?: Date;
        endDatetimeBefore?: Date;
        startDatetimeAfter?: Date;
        startDatetimeBefore?: Date;
    },
    filterByArea?: FilterByAreaDef,
) => {
    const apiClient = useMapApiClient();
    const mapId = useStable(dataName);
    const {
        debounced: { viewState, areaOnScreen },
    } = useMap(mapId);
    const { selectedContext } = useMapData(mapId);
    const currentZoom = useMemo(() => viewState?.zoom, [viewState]);
    const userHasExtraDataPermissions = useAppSelector((s) =>
        s.auth.flags.includes("enable_area_surveys"),
    );

    const extraDataLoader = useMapDataLoader<DataSurvey>({
        fetchFn: useCallback(
            async (areaToFetch, abortSignal, cursor) => {
                const { cleanFilterValues, shortCircuit } =
                    cleanFilters<MapExtraDataSurveysListRequest>(filters);
                if (shortCircuit) {
                    return {
                        results: [] as DataSurvey[],
                    };
                }
                const response = await apiClient.mapExtraDataSurveysList(
                    {
                        ...cleanFilterValues,
                        locationWithin: areaToFetch
                            ? JSON.stringify(areaToFetch)
                            : undefined,
                        locationPreset:
                            filterByArea && filterByArea.selectedFilters
                                ? filterByArea.selectedFilters
                                : undefined,
                        cursor,
                    },
                    {
                        signal: abortSignal,
                    },
                );
                return response;
            },
            [filters, filterByArea, apiClient],
        ),
        zoomToStartFetching: 1,
        enabled:
            enabled &&
            userHasExtraDataPermissions &&
            Object.values(filters).some((v) => v),
        areaOnScreen,
        zoom: currentZoom,
        areaFilter:
            filterByArea && !filterByArea.selectedFilters
                ? filterByArea.drawnShape
                : undefined,
    });

    // When a extra data point is selected, fetch full data
    const extraDataDetailQuery = useQuery({
        queryKey: ["extraData", selectedContext.extraDataId],
        queryFn: () => {
            return apiClient.mapExtraDataSurveysRetrieve({
                id: selectedContext.extraDataId,
            });
        },
        enabled: !!selectedContext.extraDataId,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });

    const extraData = useMemo(() => {
        if (
            selectedContext.dateRangeToShowData?.start &&
            selectedContext.dateRangeToShowData?.end
        ) {
            const startDate = new Date(
                selectedContext.dateRangeToShowData.start,
            );
            const endDate = new Date(selectedContext.dateRangeToShowData.end);
            // Show any record whose [start, end] range overlaps the selected range.
            return extraDataLoader.data.filter(
                (e) => e.startDatetime <= endDate && e.endDatetime >= startDate,
            );
        }
        return extraDataLoader.data || [];
    }, [extraDataLoader.data, selectedContext.dateRangeToShowData]);

    // Build a single point feature per survey using the survey's own location.
    const pointFeatures = useMemo(
        () =>
            extraData.map(
                (item) =>
                    ({
                        type: "Feature",
                        properties: item,
                        geometry: item.location,
                    }) as any,
            ),
        [extraData],
    );

    // Set up extra-data clustering.
    const clusterData = useMemo(() => {
        const index = new Supercluster({
            radius: 50,
            maxZoom: MAP_EXTRA_CLUSTERING_LIMIT,
        });
        index.load(pointFeatures);
        return index;
    }, [pointFeatures]);

    const clusteredPoints = useMemo(() => {
        if (!areaOnScreen) {
            return [];
        }

        if (currentZoom >= MAP_EXTRA_CLUSTERING_LIMIT) {
            return [];
        }

        // Annotate zoom level in which cluster is broken down.
        return clusterData
            .getClusters([-180, -90, 180, 90], currentZoom)
            .map((i: any) => {
                if (i.properties.cluster) {
                    i.properties.zoom = clusterData.getClusterExpansionZoom(
                        i.properties.cluster_id,
                    );
                }
                return i;
            });
    }, [clusterData, currentZoom, areaOnScreen]);

    // Layers
    const dataLayers = useMemo(() => {
        const layers = [
            ExtraDataLayer(
                createFeatureCollection(extraData).features,
                enabled,
                currentZoom ?? 0,
                undefined,
                undefined,
                extraDataDetailQuery.data?.id,
                clusteredPoints,
            ),
        ];
        // If a point is selected, also load that data as layer
        // if the provider supports it.
        if (extraDataDetailQuery.data) {
            const extraDataDetail = extraDataDetailQuery.data;
            // On ChampionX, data is stored as a FeatureCollection.
            if (extraDataDetail.dataType === "CHAMPION_X") {
                layers.push(
                    ChampionXLayer(
                        extraDataDetail.surveyData as FeatureCollection,
                        enabled,
                        "ch4",
                        `championx-${extraDataDetail.id}`,
                        undefined,
                    ),
                );
            }
        }

        return layers;
    }, [
        extraData,
        extraDataDetailQuery,
        enabled,
        currentZoom,
        clusteredPoints,
    ]);

    return {
        loading:
            (enabled && extraDataLoader.loading) ||
            (!!selectedContext.extraDataId && extraDataDetailQuery.isLoading),
        data: extraData,
        dataLayers,
    };
};
